import { createApi } from '@reduxjs/toolkit/query/react'
import { sendNotification } from 'components'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { GenericResponse, TableRequestV3, TableResponse } from 'services/types'
import {
  DevicesByIdRequest,
  DevicesByIdResponse,
  DevicesRequest,
  DevicesResponse,
  VehicleDeviceTypesRequest,
  VehicleDeviceTypesResponse,
  InfoRequest,
  InfoResponse,
  VehicleAddRequest,
  VehicleAddResponse,
  VehicleDeleteRequest,
  VehicleDeleteResponse,
  VehicleDetailRequest,
  VehicleDetailResponse,
  VehicleEditDetailRequest,
  VehicleEditDetailResponse,
  VehicleEditRequest,
  VehicleEditResponse,
  VehicleExportRequest,
  VehicleExportResponse,
  VehicleStatusRequest,
  VehicleStatusResponse,
  VehicleTypesRequest,
  VehicleTypesResponse,
  SnapshotByPlateRequest,
  SnapshotMapSettingsRequest,
  FleetResponseV2,
  FleetRequestV2,
  SnapshotByPlateResponse,
  DeviceLogsExportRequest,
  VehicleHistoriesResponse,
  VehicleHistoriesRequest,
  VehicleChangeStatusRequest,
  VehicleChangeStatusResponse,
  DeleteDeviceRequest,
  TrackHistoryRequest,
  TrackHistoryDetailRequest,
  TrackHistoryDetailResponse,
  VehicleResponse,
  VehicleRequest,
  ActivitiesRequest,
  ActivityTypesResponse,
  ActivityTypesRequest,
  ActivitiesExportRequest,
  ActivitiesExportResponse,
  RFIDTagsRequest,
  RFIDTagsResponse,
  TagsByVehicleRequest,
  TagsByVehicleResponse,
  DetachTagsRequest,
  VehicleActivitiesRequest,
  VehicleActivitiesResponse,
  ExportRFIDRequest,
  ExportRFIDResponse,
  BulkActionResponse,
  BulkDeleteRFIDRequest,
  AddRFIDTagRequest,
  AttachTagsRequest,
  SearchRFIDResponse,
  SearchRFIDRequest,
  BlackreasonsListResponse,
  VehicleListByListTypeResponse,
  VehicleListByListTypeRequest,
  AttachRFIDTagsResponse,
  AttachRFIDTagsRequest,
  DetachRFIDTagsRequest,
  ExportVehicleDevicesRequest,
  TrackHistoryResponse,
  TrackHistoryActivitiesResponse,
  TrackHistoryActivitiesRequest,
  TrackHistoryActivityDetailsRequest,
  TrackHistoryActivityDetailsResponse,
  TrackHistoryActivityTypesRequest,
  TrackHistoryActivityTypesResponse,
  FleetMapSettingsResponse,
  UpsertDeviceRequest,
  GetVehicleDeviceDetailsRequest,
  GetVehicleDeviceDetailsResponse,
  type FleetHistorySnapshotResponse,
} from './types'
import { sendExportNotification, sendImportNotification } from 'utils'
import { MutationResponse } from 'services/OpsManagement/types'
import { EventsCellType } from '@evrekadev/evreka-ui-components'
import { reportsApi } from 'services/ReportManagement/reports'
import { snapToRoads } from 'pages/FleetManagement/snapToRoads'
import { ExportRequest } from 'services/Engagement/types'

export const fleetApi = createApi({
  reducerPath: 'fleetApi',
  tagTypes: [
    'FleetSnapshots',
    'FleetList',
    'FleetDetails',
    'FleetDevices',
    'FleetHistory',
    'VehicleListByListType',
    'FleetTrackHistoryDetails',
    'FleetTrackHistory',
    'VehicleDeviceDetail',
    'Activities',
    'SnapshotByPlate',
    'VehicleDetail',
    'FleetInfo',
    'VehicleWhitelist',
    'VehicleBlacklist',
    'RFIDTags',
    'DevicesByDeviceType',
    'TelemetryData',
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/fleet/'}`,
  }),
  endpoints: (builder) => ({
    getFleetSnapshot: builder.query<FleetResponseV2, FleetRequestV2>({
      query: ({ fleetClientId, ...data }) => ({
        url: `clients/${fleetClientId}/snapshot/`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: FleetResponseV2) => {
        return response.filter((s) => !(s.latitude === 0 && s.longitude === 0))
      },
      providesTags: ['FleetSnapshots'],
    }),
    getSnapshotByPlate: builder.query<SnapshotByPlateResponse, SnapshotByPlateRequest>({
      query: ({ fleetClientId, plate, fields }) => ({
        url: `clients/${fleetClientId}/snapshot/v2/${plate}${
          fields ? `?fields=${fields.join(',')}` : ''
        }`,
        method: 'GET',
      }),
      providesTags: ['SnapshotByPlate'],
    }),
    getSnapshotMapSettings: builder.query<FleetMapSettingsResponse, SnapshotMapSettingsRequest>({
      query: ({ fleetClientId }) => ({
        url: `clients/${fleetClientId}/snapshot/v2/map_settings`,
        method: 'GET',
      }),
    }),
    getInfo: builder.query<InfoResponse, InfoRequest>({
      query: ({ clientId }) => ({
        url: `clients/${clientId}/info/`,
        method: 'GET',
      }),
      providesTags: ['FleetInfo'],
    }),
    getFleetTypes: builder.query<VehicleTypesResponse, VehicleTypesRequest>({
      query: ({ fleetClientId }) => ({
        url: `clients/${fleetClientId}/vehicle_types/`,
        method: 'GET',
      }),
    }),
    getNextFleetStatuses: builder.query<VehicleStatusResponse, VehicleStatusRequest>({
      query: ({ fleetClientId, statusId, isEditMode }) => {
        const all = isEditMode ? 1 : undefined
        return {
          url: `clients/${fleetClientId}/vehicle_status/`,
          method: 'GET',
          params: {
            all,
            id: statusId,
          },
        }
      },
    }),
    changeVehicleStatus: builder.mutation<VehicleChangeStatusResponse, VehicleChangeStatusRequest>({
      query: ({ fleetClientId, ...data }) => ({
        url: `clients/${fleetClientId}/change_vehicle_status/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, { vehicle_id }) => [
        { type: 'FleetList' },
        { type: 'VehicleDetail', id: vehicle_id },
        'SnapshotByPlate',
        'FleetDetails',
      ],
    }),
    getFleetList: builder.query<TableResponse<EventsCellType>, TableRequestV3>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/vehicles/`,
        method: 'POST',
        data,
      }),
      providesTags: ['FleetList'],
    }),
    getActivityList: builder.query<TableResponse<EventsCellType>, ActivitiesRequest>({
      query: ({ clientId, type, ...data }) => ({
        url: `clients/${clientId}/activities/`,
        method: 'POST',
        data,
        params: {
          type,
        },
      }),
      providesTags: ['Activities'],
    }),
    exportVehicleDevices: builder.mutation<MutationResponse, ExportVehicleDevicesRequest>({
      query({ clientId, vehicleId, ...data }) {
        return {
          url: `clients/${clientId}/vehicles/${vehicleId}/export_devices/`,
          method: 'POST',
          data,
        }
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    attachRFIDTags: builder.mutation<AttachRFIDTagsResponse, AttachRFIDTagsRequest>({
      query: ({ fleetClientId, vehicles, tags }) => ({
        url: `clients/${fleetClientId}/vehicles/attach_rfid_tags/`,
        method: 'POST',
        data: {
          vehicles,
          tags,
        },
      }),
      invalidatesTags: ['RFIDTags', 'VehicleListByListType'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    detachRFIDTags: builder.mutation<AttachRFIDTagsResponse, DetachRFIDTagsRequest>({
      query: ({ fleetClientId, vehicles, tags }) => ({
        url: `clients/${fleetClientId}/vehicles/detach_rfid_tags/`,
        method: 'POST',
        data: {
          vehicles,
          tags,
        },
      }),
      invalidatesTags: ['RFIDTags', 'VehicleListByListType'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getActivityTypes: builder.query<ActivityTypesResponse, ActivityTypesRequest>({
      query: ({ fleetClientId }) => ({
        url: `clients/${fleetClientId}/activity_types/`,
        method: 'GET',
      }),
    }),
    getEditVehicleDetail: builder.query<VehicleEditDetailResponse, VehicleEditDetailRequest>({
      query: ({ fleetClientId, vehicleId, errorOverride }) => ({
        url: `clients/${fleetClientId}/edit_vehicle_detail/${vehicleId}`,
        method: 'GET',
        errorOverride: errorOverride,
      }),
      providesTags: ['FleetDetails'],
    }),
    getFleetContentFields: builder.query<any, string>({
      query: (fleetClientId) => ({
        url: `clients/${fleetClientId}/get_vehicle_content_fields/`,
        method: 'GET',
      }),
    }),
    addFleet: builder.mutation<VehicleAddResponse, VehicleAddRequest>({
      query({ fleetClientId, body }) {
        return {
          url: `clients/${fleetClientId}/create_vehicle/`,
          method: 'POST',
          data: body,
        }
      },
      invalidatesTags: [
        'FleetList',
        'VehicleDetail',
        'SnapshotByPlate',
        'VehicleListByListType',
        'FleetDetails',
        'FleetSnapshots',
        'FleetInfo',
        'FleetTrackHistoryDetails',
        'FleetTrackHistory',
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    editVehicle: builder.mutation<VehicleEditResponse, VehicleEditRequest>({
      query({ fleetClientId, body }) {
        return {
          url: `clients/${fleetClientId}/edit_vehicle/`,
          method: 'POST',
          data: body,
        }
      },
      invalidatesTags: [
        'FleetList',
        'VehicleDetail',
        'SnapshotByPlate',
        'VehicleListByListType',
        'FleetDetails',
        'FleetSnapshots',
        'FleetInfo',
        'FleetTrackHistoryDetails',
        'FleetTrackHistory',
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    deleteVehicle: builder.mutation<VehicleDeleteResponse, VehicleDeleteRequest>({
      query: ({ fleetClientId, vehicleId }) => ({
        url: `clients/${fleetClientId}/delete_vehicle/${vehicleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FleetList', 'FleetInfo', 'VehicleListByListType', 'FleetSnapshots'],
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    exportVehicleList: builder.mutation<VehicleExportResponse, VehicleExportRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/export_vehicles/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    exportDeviceLogs: builder.mutation<GenericResponse, DeviceLogsExportRequest>({
      query: ({ fleetClientId, ...data }) => ({
        url: `clients/${fleetClientId}/export_device_logs/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    exportActivityList: builder.mutation<ActivitiesExportResponse, ActivitiesExportRequest>({
      query: ({ clientId, type, ...data }) => ({
        url: `clients/${clientId}/export_activities/?type=${type}`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    exportSpeedAlertActivityList: builder.mutation<ActivitiesExportResponse, ExportRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/speed_alert/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getVehicleDetail: builder.query<VehicleDetailResponse, VehicleDetailRequest>({
      query: ({ fleetClientId, vehicleId }) => ({
        url: `clients/${fleetClientId}/vehicle_detail/${vehicleId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { vehicleId }) => [{ type: 'VehicleDetail', id: vehicleId }],
    }),
    getTrackHistoryActivities: builder.query<
      TrackHistoryActivitiesResponse,
      TrackHistoryActivitiesRequest
    >({
      query: ({ fleetClientId, ...data }) => ({
        url: `clients/${fleetClientId}/activities/map`,
        method: 'POST',
        data,
      }),
    }),
    getTrackHistoryActivityDetails: builder.query<
      TrackHistoryActivityDetailsResponse,
      TrackHistoryActivityDetailsRequest
    >({
      query: ({ fleetClientId, activityId }) => ({
        url: `clients/${fleetClientId}/activities/map/${activityId}`,
        method: 'GET',
      }),
    }),
    getTrackHistoryActivityTypes: builder.query<
      TrackHistoryActivityTypesResponse,
      TrackHistoryActivityTypesRequest
    >({
      query: ({ fleetClientId }) => ({
        url: `clients/${fleetClientId}/activity_types/`,
        method: 'GET',
      }),
    }),
    getFleetActivities: builder.query<VehicleActivitiesResponse, VehicleActivitiesRequest>({
      query: ({ fleetClientId, vehicleId, ...data }) => ({
        url: `clients/${fleetClientId}/vehicles/${vehicleId}/activities/`,
        method: 'POST',
        data,
      }),
    }),
    getDevices: builder.query<DevicesResponse, DevicesRequest>({
      query: ({ clientId, device_type }) => ({
        url: `clients/${clientId}/devices/?device_type=${device_type}`,
        method: 'GET',
      }),
      providesTags: ['DevicesByDeviceType'],
    }),
    getFleetDevices: builder.query<DevicesByIdResponse, DevicesByIdRequest>({
      query: ({ fleetClientId, vehicleId, ...data }) => ({
        url: `clients/${fleetClientId}/vehicles/${vehicleId}/devices/`,
        method: 'POST',
        data,
      }),
      providesTags: ['FleetDevices'],
    }),
    getDeviceTypes: builder.query<VehicleDeviceTypesResponse, VehicleDeviceTypesRequest>({
      query: () => ({
        url: `device_types/`,
        method: 'GET',
      }),
    }),
    upsertDevice: builder.mutation<GenericResponse, UpsertDeviceRequest>({
      query: ({ fleetClientId, ...data }) => ({
        url: `clients/${fleetClientId}/upsert_vehicle_device/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'FleetList',
        'VehicleDetail',
        'FleetDevices',
        'FleetDetails',
        'VehicleDeviceDetail',
        'FleetSnapshots',
        'FleetHistory',
        'FleetTrackHistoryDetails',
        'FleetTrackHistory',
        'DevicesByDeviceType',
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    deleteDevice: builder.mutation<GenericResponse, DeleteDeviceRequest>({
      query: ({ fleetClientId, vehicleId, ...data }) => ({
        url: `clients/${fleetClientId}/vehicles/${vehicleId}/delete_device/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'FleetList',
        'VehicleDetail',
        'FleetDevices',
        'FleetDetails',
        'VehicleDeviceDetail',
        'FleetSnapshots',
        'FleetHistory',
        'FleetTrackHistoryDetails',
        'FleetTrackHistory',
      ],
    }),
    getVehicleDeviceDetails: builder.query<
      GetVehicleDeviceDetailsResponse,
      GetVehicleDeviceDetailsRequest
    >({
      query: ({ fleetClientId, vehicleId, deviceId }) => ({
        url: `clients/${fleetClientId}/vehicles/${vehicleId}/devices/${deviceId}/`,
        method: 'GET',
      }),
      providesTags: ['VehicleDeviceDetail'],
    }),
    getVehicleHistories: builder.query<VehicleHistoriesResponse, VehicleHistoriesRequest>({
      query: ({ fleetClientId, vehicle_id, ...data }) => ({
        url: `clients/${fleetClientId}/vehicles/${vehicle_id}/history/`,
        method: 'POST',
        data,
      }),
      providesTags: ['FleetHistory'],
    }),
    getTrackingHistory: builder.query<TrackHistoryResponse, TrackHistoryRequest>({
      query: ({ fleetClientId, ...data }) => ({
        url: `clients/${fleetClientId}/history/`,
        method: 'POST',
        data,
      }),
      providesTags: ['FleetTrackHistory'],
      transformResponse: async (response: TrackHistoryResponse, _, arg) => {
        if (arg.fleetClientId === '822b98da-4817-4217-b18d-ee8ef558ae8b') {
          const result = await snapToRoads(response.data)
          return {
            ...response,
            data: result,
          }
        }
        return {
          ...response,
          data: response.data.filter((item) => !(item.latitude === 0 && item.longitude === 0)),
        }
      },
    }),
    getTrackingHistoryDetail: builder.query<TrackHistoryDetailResponse, TrackHistoryDetailRequest>({
      query: ({ fleetClientId, id }) => ({
        url: `clients/${fleetClientId}/history/${id}`,
        method: 'GET',
      }),
      providesTags: ['FleetTrackHistoryDetails'],
    }),
    getVehiclesBySearch: builder.mutation<VehicleResponse, VehicleRequest>({
      query: ({ clientId, vehicle }) => ({
        url: `clients/${clientId}/vehicle-search/?q=${vehicle}`,
        method: 'GET',
      }),
    }),
    getRFIDTags: builder.query<RFIDTagsResponse, RFIDTagsRequest>({
      query: ({ fleetClientId, ...data }) => ({
        url: `clients/${fleetClientId}/rfid/tags/`,
        method: 'POST',
        data,
      }),
      providesTags: ['RFIDTags'],
    }),
    addRFIDTag: builder.mutation<MutationResponse, AddRFIDTagRequest>({
      query: ({ fleetClientId, body }) => ({
        url: `clients/${fleetClientId}/rfid/create/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['RFIDTags', 'VehicleWhitelist', 'VehicleBlacklist'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    attachTagsToVehicle: builder.mutation<GenericResponse, AttachTagsRequest>({
      query: ({ clientId, type: _type, ...data }) => ({
        url: `clients/${clientId}/vehicles/attach_rfid_tags/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['VehicleWhitelist', 'VehicleBlacklist'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            sendNotification({
              type: 'success',
              toastContent: data.detail.message,
            }) as any,
          )
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    searchRFIDTag: builder.mutation<SearchRFIDResponse, SearchRFIDRequest>({
      query: ({ fleetClientId, query_param }) => ({
        url: `clients/${fleetClientId}/rfid/tags/search/?q=${query_param}`,
        method: 'GET',
      }),
    }),
    getBlacklistReasons: builder.query<BlackreasonsListResponse, string>({
      query: () => ({
        url: `rfid/blacklist_reasons/`,
        method: 'GET',
      }),
    }),
    getVehicleListByListType: builder.query<
      VehicleListByListTypeResponse,
      VehicleListByListTypeRequest
    >({
      query: ({ fleetClientId, type, tags }) => ({
        url: `clients/${fleetClientId}/rfid/vehicles/`,
        method: 'POST',
        data: { type: type, tags: tags },
      }),
      providesTags: ['VehicleListByListType'],
    }),
    bulkDeleteRFID: builder.mutation<BulkActionResponse, BulkDeleteRFIDRequest>({
      query: ({ fleetClientId, ...data }) => ({
        url: `clients/${fleetClientId}/rfid/tags/delete/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['RFIDTags', 'VehicleWhitelist', 'VehicleBlacklist'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    exportRFIDList: builder.mutation<ExportRFIDResponse, ExportRFIDRequest>({
      query: ({ fleetClientId, ...data }) => ({
        url: `clients/${fleetClientId}/rfid/export_tags/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    importRFIDList: builder.mutation<MutationResponse, { fleetClientId: string; body: FormData }>({
      query: ({ fleetClientId, body }) => ({
        url: `clients/${fleetClientId}/rfid/import/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['RFIDTags'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendImportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ImportReports']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getTagListByVehicle: builder.query<TagsByVehicleResponse, TagsByVehicleRequest>({
      query: ({ fleetClientId, vehicleId, type, ...data }) => ({
        url: `clients/${fleetClientId}/vehicles/${vehicleId}/rfid_tags/?type=${type}`,
        method: 'POST',
        data,
      }),
      providesTags: ['VehicleWhitelist', 'VehicleBlacklist'],
    }),
    detachTagsFromVehicle: builder.mutation<GenericResponse, DetachTagsRequest>({
      query: ({ clientId, type: _type, ...data }) => ({
        url: `clients/${clientId}/vehicles/detach_rfid_tags/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['VehicleWhitelist', 'VehicleBlacklist'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getTelemetryDataByRecordId: builder.query<
      FleetHistorySnapshotResponse,
      { recordId: string; fleetClientId: string }
    >({
      query: ({ fleetClientId, recordId }) => ({
        url: `clients/${fleetClientId}/history/${recordId}`,
        method: 'GET',
      }),
      providesTags: ['TelemetryData'],
    }),
    getVehicleDropdownList: builder.query<
      Array<{
        value: string
        label: string
        plate: string
      }>,
      { clientId: number }
    >({
      query: ({ clientId }) => ({
        url: `external/clients/${clientId}/vehicle_dropdown_list/`,
        method: 'GET',
      }),
    }),
  }),
})
